import ReactQuill from "react-quill";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LEFT_OFFSET, TEXT_PALETTE, BG_PALETTE } from "./constants";
import { registerCustomFontFormat } from "./quillConfig";

import styles from "./FloatingTextEditor.module.css";
import "./QuillWrapper.css";

interface FloatingTextEditorProps {
  value: string;
  id: string;
  onChange: (value: string) => void;
  props?: React.ComponentProps<typeof ReactQuill>;
  onFocus: () => void;
  active: boolean;
  font: string | string[];
  setSelectedFont: (font: string) => void;
}

interface ColorSwatchGridProps {
  colors: (string | null)[];
  onColorSelect: (color: string) => void;
  type: string;
}

interface FontDropdownProps {
  fonts: string[];
  onFontSelect: (font: string) => void;
}

interface CustomToolbarProps {
  id: string;
  toolbarPosition: { top: number; left: number };
  showToolbar: boolean;
  showColorPicker: boolean;
  showBgPicker: boolean;
  showFontPicker?: boolean;
  applyColor: (color: string) => void;
  applyBackground: (color: string) => void;
  applyFont: (font: string) => void;
  setShowColorPicker: (show: boolean) => void;
  setShowBgPicker: (show: boolean) => void;
  setShowFontPicker?: (show: boolean) => void;
  font: string | string[];
  setSelectedFont: (font: string) => void;
}

function BGIcon(props: Readonly<React.SVGProps<SVGSVGElement>>) {
  return (
    <svg viewBox="0 0 18 18" width={18} height={18} fill="#fff" {...props}>
      <g className="ql-fill ql-color-label">
        <polygon points="6 6.868 6 6 5 6 5 7 5.942 7 6 6.868" />
        <rect height={1} width={1} x={4} y={4} />
        <polygon points="6.817 5 6 5 6 6 6.38 6 6.817 5" />
        <rect height={1} width={1} x={2} y={6} />
        <rect height={1} width={1} x={3} y={5} />
        <rect height={1} width={1} x={4} y={7} />
        <polygon points="4 11.439 4 11 3 11 3 12 3.755 12 4 11.439" />
        <rect height={1} width={1} x={2} y={12} />
        <rect height={1} width={1} x={2} y={9} />
        <rect height={1} width={1} x={2} y={15} />
        <polygon points="4.63 10 4 10 4 11 4.192 11 4.63 10" />
        <rect height={1} width={1} x={3} y={8} />
        <path d="M10.832,4.2L11,4.582V4H10.708A1.948,1.948,0,0,1,10.832,4.2Z" />
        <path d="M7,4.582L7.168,4.2A1.929,1.929,0,0,1,7.292,4H7V4.582Z" />
        <path d="M8,13H7.683l-0.351.8a1.933,1.933,0,0,1-.124.2H8V13Z" />
        <rect height={1} width={1} x={12} y={2} />
        <rect height={1} width={1} x={11} y={3} />
        <path d="M9,3H8V3.282A1.985,1.985,0,0,1,9,3Z" />
        <rect height={1} width={1} x={2} y={3} />
        <rect height={1} width={1} x={6} y={2} />
        <rect height={1} width={1} x={3} y={2} />
        <rect height={1} width={1} x={5} y={3} />
        <rect height={1} width={1} x={9} y={2} />
        <rect height={1} width={1} x={15} y={14} />
        <polygon points="13.447 10.174 13.469 10.225 13.472 10.232 13.808 11 14 11 14 10 13.37 10 13.447 10.174" />
        <rect height={1} width={1} x={13} y={7} />
        <rect height={1} width={1} x={15} y={5} />
        <rect height={1} width={1} x={14} y={6} />
        <rect height={1} width={1} x={15} y={8} />
        <rect height={1} width={1} x={14} y={9} />
        <path d="M3.775,14H3v1H4V14.314A1.97,1.97,0,0,1,3.775,14Z" />
        <rect height={1} width={1} x={14} y={3} />
        <polygon points="12 6.868 12 6 11.62 6 12 6.868" />
        <rect height={1} width={1} x={15} y={2} />
        <rect height={1} width={1} x={12} y={5} />
        <rect height={1} width={1} x={13} y={4} />
        <polygon points="12.933 9 13 9 13 8 12.495 8 12.933 9" />
        <rect height={1} width={1} x={9} y={14} />
        <rect height={1} width={1} x={8} y={15} />
        <path d="M6,14.926V15H7V14.316A1.993,1.993,0,0,1,6,14.926Z" />
        <rect height={1} width={1} x={5} y={15} />
        <path d="M10.668,13.8L10.317,13H10v1h0.792A1.947,1.947,0,0,1,10.668,13.8Z" />
        <rect height={1} width={1} x={11} y={15} />
        <path d="M14.332,12.2a1.99,1.99,0,0,1,.166.8H15V12H14.245Z" />
        <rect height={1} width={1} x={14} y={15} />
        <rect height={1} width={1} x={15} y={11} />
      </g>
      <polyline className="ql-stroke" points="5.5 13 9 5 12.5 13" />
      <line className="ql-stroke" x1={11.63} x2={6.38} y1={11} y2={11} />
    </svg>
  );
}

function TransparentBgIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 19 22" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="10" r="9" stroke="red" strokeWidth="2" fill="none" />
      <line x1="4" y1="16" x2="16" y2="4" stroke="red" strokeWidth="2" />
    </svg>
  );
}

function FontIcon(props: Readonly<React.SVGProps<SVGSVGElement>>) {
  return (
    <svg viewBox="0 0 18 18" width={18} height={18} fill="#fff" {...props}>
      <path d="M5,4V8H6V5H7V8H8V4H9V3H4V4H5Z M11,8H13A1,1 0 0,0 14,7V6A1,1 0 0,0 13,5H11A1,1 0 0,0 10,6V7A1,1 0 0,0 11,8M11,6H13V7H11V6Z M5,10V14H6V11H7V14H8V10H9V9H4V10H5Z M11,14H13A1,1 0 0,0 14,13V12A1,1 0 0,0 13,11H11A1,1 0 0,0 10,12V13A1,1 0 0,0 11,14M11,12H13V13H11V12Z" />
    </svg>
  );
}

export function FloatingTextEditor({
  value,
  onChange,
  props,
  id,
  onFocus: handleFocus,
  active,
  font,
  setSelectedFont,
}: Readonly<FloatingTextEditorProps>) {
  const quillRef = useRef<ReactQuill>(null);
  const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
  const [showToolbar, setShowToolbar] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showBgPicker, setShowBgPicker] = useState(false);
  const [showFontPicker, setShowFontPicker] = useState(false);

  /**
   * Local editor value that decouples ReactQuill from the store.
   *
   * ReactQuill can't round-trip styled HTML: when it receives a `value` prop
   * it re-parses it through `clipboard.convert`, which strips inline styles
   * and custom classes. It then fires `text-change` (source="api") with the
   * stripped content, creating an infinite reconciliation loop because the
   * stripped value never matches the styled prop.
   *
   * By keeping a local value we only feed ReactQuill content it can handle,
   * and sync from the external `value` prop only when the editor is idle
   * (not focused / not actively being edited by the user).
   */
  const [editorValue, setEditorValue] = useState(value);
  const isEditorFocused = useRef(false);

  useEffect(() => {
    if (!isEditorFocused.current) {
      setEditorValue(value);
    }
  }, [value]);

  let timer: NodeJS.Timeout;

  const handleResetToolbar = () => {
    setShowToolbar(false);
    setShowColorPicker(false);
    setShowBgPicker(false);
    setShowFontPicker(false);
  };

  if (!active && showToolbar) {
    handleResetToolbar();
  }

  const handleBlur = () => {
    if (timer) {
      clearTimeout(timer);
    }

    isEditorFocused.current = false;
    setEditorValue(value);
    handleResetToolbar();
  };

  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const editorElement = quill.root;

      // Register custom font format to preserve font classes
      // without this after closing and re opening the advanced panel
      // the selected fonts will be lost.
      if (Array.isArray(font)) {
        registerCustomFontFormat(font);
      }

      const blockFormatting = (e: KeyboardEvent) => {
        if (e.ctrlKey || e.metaKey) {
          const blockedKeys = ["b", "i", "u", "s"];
          if (blockedKeys.includes(e.key.toLowerCase())) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return false;
          }
        }
      };

      editorElement.addEventListener("keydown", blockFormatting, true);

      return () => {
        editorElement.removeEventListener("keydown", blockFormatting, true);
      };
    }
  }, []);

  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();

      quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node) => {
        const plaintext = node.innerText || node.textContent || "";
        const Delta = (quill.constructor as any).import("delta");

        return new Delta().insert(plaintext);
      });
    }
  }, []);

  const handleSelectionChange = (range: ReactQuill.Range) => {
    if (range && quillRef.current) {
      timer = setTimeout(() => {
        const editorRect = quillRef.current?.getEditingArea()?.getBoundingClientRect();
        if (editorRect) {
          // Adjust left offset based on whether font picker is available
          const isMultipleFonts = Array.isArray(font);
          const adjustedOffset = isMultipleFonts ? LEFT_OFFSET + 40 : LEFT_OFFSET;

          setToolbarPosition({
            top: editorRect.top,
            left: editorRect.left - adjustedOffset,
          });
        }
        setShowToolbar(true);
      }, 500);
    } else if (!range) {
      handleBlur();
    }
  };

  const applyColor = (color: string) => {
    if (quillRef.current) {
      quillRef.current.getEditor().format("color", color, "user");
      setShowColorPicker(false);
    }
  };

  const applyBackground = (color: string) => {
    if (quillRef.current) {
      quillRef.current.getEditor().format("background", color, "user");
      setShowBgPicker(false);
    }
  };

  const applyFont = (fontFamily: string) => {
    if (quillRef.current) {
      quillRef.current.getEditor().format("font", fontFamily, "user");
      setShowFontPicker(false);
      setSelectedFont(fontFamily);
    }
  };

  return (
    <>
      <div className="floating-text-editor-wrapper">
        <ReactQuill
          id={`floating-text-editor-${id}`}
          ref={quillRef}
          value={editorValue}
          theme="bubble"
          modules={{
            toolbar: false,
            clipboard: { matchVisual: false },
          }}
          formats={["font", "color", "background", "fontClass"]}
          className={styles.editor}
          onChange={(content: string, _delta: unknown, source: string) => {
            if (source === "user") {
              setEditorValue(content);
              onChange(content);
            }
          }}
          onChangeSelection={handleSelectionChange}
          onBlur={handleBlur}
          onFocus={() => {
            isEditorFocused.current = true;
            handleFocus();
          }}
          preserveWhitespace
          {...props}
        />
      </div>

      {createPortal(
        <CustomToolbar
          id={id}
          toolbarPosition={toolbarPosition}
          showToolbar={showToolbar}
          showColorPicker={showColorPicker}
          showBgPicker={showBgPicker}
          showFontPicker={showFontPicker}
          applyColor={applyColor}
          applyBackground={applyBackground}
          applyFont={applyFont}
          setShowColorPicker={setShowColorPicker}
          setShowBgPicker={setShowBgPicker}
          setShowFontPicker={setShowFontPicker}
          font={font}
          setSelectedFont={setSelectedFont}
        />,
        document.getElementById("portal")!,
      )}
    </>
  );
}

function ColorSwatchGrid({ colors, onColorSelect, type }: Readonly<ColorSwatchGridProps>) {
  return (
    <div className={styles.colorGrid}>
      {colors.map((color) => {
        if (!color) {
          return <span key={`${type}-empty`} />;
        }

        if (color === "transparent") {
          return (
            <button
              key={`${type}-${color}`}
              onClick={() => onColorSelect(color)}
              className={styles.colorGridButton}
              style={{ backgroundColor: "transparent" }}
              title="No background"
            >
              <TransparentBgIcon />
            </button>
          );
        }

        return (
          <button
            key={`${type}-${color}`}
            onClick={() => onColorSelect(color)}
            className={styles.colorGridButton}
            style={{ backgroundColor: color, gridColumn: color === "#000000" ? "9/-1" : "auto" }}
            title={color}
          />
        );
      })}
    </div>
  );
}

function FontDropdown({ fonts, onFontSelect }: Readonly<FontDropdownProps>) {
  return (
    <div className={styles.fontDropdown}>
      {fonts.map((font) => (
        <button
          key={font}
          onClick={() => onFontSelect(font)}
          className={styles.fontOption}
          style={{ fontFamily: font }}
          title={font}
        >
          {font}
        </button>
      ))}
    </div>
  );
}

function CustomToolbar({
  id,
  toolbarPosition,
  showToolbar,
  showColorPicker,
  showBgPicker,
  showFontPicker,
  applyColor,
  applyBackground,
  applyFont,
  setShowColorPicker,
  setShowBgPicker,
  setShowFontPicker,
  font,
  setSelectedFont,
}: Readonly<CustomToolbarProps>) {
  const isMultipleFonts = Array.isArray(font);
  const availableFonts = isMultipleFonts ? font : [];

  const handleFontSelect = (fontFamily: string) => {
    applyFont(fontFamily);
    setSelectedFont(fontFamily);
  };

  return (
    <div
      id={`floating-text-editor-${id}-toolbar`}
      className={styles.customToolbar}
      style={{
        top: toolbarPosition.top,
        left: toolbarPosition.left,
        display: showToolbar ? "flex" : "none",
      }}
    >
      <button
        title="Text Color"
        onClick={() => {
          setShowColorPicker(!showColorPicker);
          setShowBgPicker(false);
          setShowFontPicker?.(false);
        }}
        className={styles.colorPicker}
      >
        A
      </button>
      {showColorPicker && <ColorSwatchGrid colors={TEXT_PALETTE} onColorSelect={applyColor} type="color" />}

      <button
        title="Background Color"
        onClick={() => {
          setShowBgPicker(!showBgPicker);
          setShowColorPicker(false);
          setShowFontPicker?.(false);
        }}
        className={styles.backgroundPicker}
      >
        <BGIcon />
      </button>
      {showBgPicker && <ColorSwatchGrid colors={BG_PALETTE} onColorSelect={applyBackground} type="background" />}

      {isMultipleFonts && (
        <>
          <button
            title="Font Family"
            onClick={() => {
              setShowFontPicker?.(!showFontPicker);
              setShowColorPicker(false);
              setShowBgPicker(false);
            }}
            className={styles.fontPicker}
          >
            <FontIcon />
          </button>
          {showFontPicker && <FontDropdown fonts={availableFonts} onFontSelect={handleFontSelect} />}
        </>
      )}
    </div>
  );
}
